import { Center, Flex, Img, Stack, useColorModeValue } from "@chakra-ui/react"
import { useState } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { AuthCard, EmailInputStep, PinVerificationStep, SuccessMessageStep } from "../components"
import { RecoverRequest, RecoverVerify } from "../../../services/AuthService"

type Step = 'email' | 'pin' | 'success'

const ForgotPasswordPage = () => {
    const [step, setStep] = useState<Step>('email')
    const [email, setEmail] = useState<string>('')
    const [pin, setPin] = useState<string>('')
    const [is_loading, setIsLoading] = useState<boolean>(false)
    const [code, setCode] = useState<string>('')
    const [access_token, setAccessToken] = useState<string>('')
    const [error, setError] = useState<string>('')
    const navigate = useNavigate()
    const [searchParams] = useSearchParams()
    const buttonColor = useColorModeValue("rgb(0, 174, 239)", "rgb(0, 174, 239)")

    const submitEmail = async () => {
        setIsLoading(true)
        setError('')
        
        try {
            const app_id = searchParams.get('app_id')
            const response = await RecoverRequest(email, app_id || undefined)
            
            setCode(response.code)
            setAccessToken(response.access_token)
            setStep('pin')
        } catch (err: any) {
            setError(err.message || 'Failed to send recovery code')
        } finally {
            setIsLoading(false)
        }
    }

    const submitPin = async () => {
        setIsLoading(true)
        setError('')
        
        try {
            await RecoverVerify(code, access_token, pin)
            setStep('success')
        } catch (err: any) {
            setError('Incorrect pin')
        } finally {
            setIsLoading(false)
        }
    }

    const handleEmailChange = (value: string) => {
        setEmail(value)
        if (error && step === 'email') {
            setError('')
        }
    }

    const handlePinChange = (value: string) => {
        setPin(value)
        if (error) {
            setError('')
        }
    }

    const backToLogin = () => {
        const app_id = searchParams.get('app_id')
        const redirect_url = searchParams.get('redirect_url')
        const params = new URLSearchParams()
        if (app_id) params.set('app_id', app_id)
        if (redirect_url) params.set('redirect_url', redirect_url)
        navigate(`/login${params.toString() ? `?${params.toString()}` : ''}`)
    }

    return (
        <Flex
            minH={'100vh'}
            align={'center'}
            justify={'center'}
            bg={'rgb(0, 174, 239)'}>
            <Stack spacing={8} mx={'auto'} maxW={'lg'} py={12} px={6}>
                <Center w={'450px'}>
                    <Img src={'/images/as-logo-white.png'} w={'160px'} />
                </Center>
                <AuthCard>
                    {step === 'email' && (
                        <EmailInputStep
                            email={email}
                            setEmail={handleEmailChange}
                            onSubmit={submitEmail}
                            onBackToLogin={backToLogin}
                            isLoading={is_loading}
                            buttonColor={buttonColor}
                            errorMessage={error}
                        />
                    )}

                    {step === 'pin' && (
                        <PinVerificationStep
                            email={email}
                            pin={pin}
                            setPin={handlePinChange}
                            onSubmit={submitPin}
                            onBackToLogin={backToLogin}
                            isLoading={is_loading}
                            buttonColor={buttonColor}
                            hasError={!!error}
                            errorMessage={error}
                        />
                    )}

                    {step === 'success' && (
                        <SuccessMessageStep
                            onBackToLogin={backToLogin}
                            buttonColor={buttonColor}
                        />
                    )}
                </AuthCard>
            </Stack>
        </Flex>
    )
}

export default ForgotPasswordPage